import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ProductsService } from '../../../shared/service/product.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-confirm-account',
  templateUrl: './confirm-account.component.html',
  styleUrls: ['./confirm-account.component.scss']
})
export class ConfirmAccountComponent implements OnInit {
  
  confirmForm: FormGroup;
  confirmationToken: string = '';
  showPassword: boolean = false;
  showConfirmPassword: boolean = false;
  isLoading: boolean = false;
  passwordStrengthScore: number = 0;
  passwordRequirements = {
    minLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSpecialChar: false
  };

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private productsService: ProductsService
  ) {
    this.confirmForm = this.fb.group({
      password: ['', [
        Validators.required,
        Validators.minLength(8),
        this.passwordStrengthValidator.bind(this)
      ]],
      confirmPassword: ['', [Validators.required]]
    }, {
      validators: this.passwordMatchValidator
    });
  }

  ngOnInit(): void {
    // Obtener el token de confirmación de la URL
    this.route.params.subscribe(params => {
      this.confirmationToken = params['token'];
      console.log('Token recibido:', this.confirmationToken);
      
      // ✅ DETECTAR TIPO DE RUTA PARA LOGGING
      const currentUrl = this.router.url;
      if (currentUrl.includes('/activate/')) {
        console.log('✅ [ACTIVATE] Usuario llegó via ruta de activación de cuenta');
      } else if (currentUrl.includes('/confirm/')) {
        console.log('✅ [CONFIRM] Usuario llegó via ruta de confirmación de cuenta');
      }
      
      // ✅ TEMPORAL: Permitir acceso sin token para pruebas
      if (!this.confirmationToken || this.confirmationToken.trim() === '') {
        console.warn('⚠️ [TEMPORAL] Acceso sin token - Solo para pruebas');
        this.confirmationToken = 'DEMO_TOKEN_FOR_TESTING'; // Token temporal para pruebas
      } else if (this.confirmationToken.length < 32 && this.confirmationToken !== 'DEMO_TOKEN_FOR_TESTING') {
        // Validación básica de longitud del token (excepto para token de prueba)
        this.showErrorAndRedirect('Token de confirmación inválido');
      }
    });

    // Escuchar cambios en el campo de contraseña para evaluar su fortaleza
    this.confirmForm.get('password')?.valueChanges.subscribe(password => {
      this.evaluatePasswordStrength(password);
    });
  }

  // Validador personalizado para la fortaleza de la contraseña (OWASP)
  passwordStrengthValidator(control: AbstractControl): {[key: string]: any} | null {
    const password = control.value;
    if (!password) return null;

    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
    const minLength = password.length >= 8;

    const isValid = hasUppercase && hasLowercase && hasNumber && hasSpecialChar && minLength;

    if (!isValid) {
      return { 'weakPassword': true };
    }

    return null;
  }

  // Validador para confirmar que las contraseñas coinciden
  passwordMatchValidator(group: FormGroup): {[key: string]: any} | null {
    const password = group.get('password')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;
    
    if (password !== confirmPassword) {
      return { 'passwordMismatch': true };
    }
    
    return null;
  }

  // Evaluar la fortaleza de la contraseña
  evaluatePasswordStrength(password: string): void {
    if (!password) {
      this.passwordStrengthScore = 0;
      this.resetPasswordRequirements();
      return;
    }

    this.passwordRequirements.minLength = password.length >= 8;
    this.passwordRequirements.hasUppercase = /[A-Z]/.test(password);
    this.passwordRequirements.hasLowercase = /[a-z]/.test(password);
    this.passwordRequirements.hasNumber = /[0-9]/.test(password);
    this.passwordRequirements.hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);

    // Calcular score de fortaleza
    let score = 0;
    if (this.passwordRequirements.minLength) score += 20;
    if (this.passwordRequirements.hasUppercase) score += 20;
    if (this.passwordRequirements.hasLowercase) score += 20;
    if (this.passwordRequirements.hasNumber) score += 20;
    if (this.passwordRequirements.hasSpecialChar) score += 20;

    this.passwordStrengthScore = score;
  }

  resetPasswordRequirements(): void {
    this.passwordRequirements = {
      minLength: false,
      hasUppercase: false,
      hasLowercase: false,
      hasNumber: false,
      hasSpecialChar: false
    };
  }

  // Obtener clase CSS para la barra de fortaleza
  getPasswordStrengthClass(): string {
    if (this.passwordStrengthScore <= 40) return 'weak';
    if (this.passwordStrengthScore <= 80) return 'medium';
    return 'strong';
  }

  // Obtener texto de fortaleza
  getPasswordStrengthText(): string {
    if (this.passwordStrengthScore <= 40) return 'Débil';
    if (this.passwordStrengthScore <= 80) return 'Media';
    return 'Fuerte';
  }

  // Toggle visibilidad de contraseña
  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  // Enviar formulario
  onSubmit(): void {
    if (this.confirmForm.invalid) {
      this.markFormGroupTouched();
      return;
    }

    this.isLoading = true;

    const password = this.confirmForm.get('password')?.value;

    // ✅ TEMPORAL: Manejar token de prueba
    if (this.confirmationToken === 'DEMO_TOKEN_FOR_TESTING') {
      console.warn('⚠️ [TEMPORAL] Simulando activación de cuenta - Solo para pruebas');
      
      // Simular respuesta exitosa después de 2 segundos
      setTimeout(() => {
        this.isLoading = false;
        
        Swal.fire({
          title: '¡Cuenta confirmada! (DEMO)',
          text: 'Contraseña establecida correctamente en modo de prueba.',
          icon: 'success',
          confirmButtonText: 'Continuar',
          confirmButtonColor: '#28a745'
        }).then(() => {
          console.log('🎯 [DEMO] Contraseña establecida:', password);
          // No redirigir en modo demo, solo mostrar mensaje
        });
      }, 2000);
      
      return;
    }

    // Llamada normal a la API /auth/activate/{token}
    this.productsService.activateAccount(this.confirmationToken, password).subscribe({
      next: (response) => {
        console.log('Cuenta activada exitosamente:', response);
        this.isLoading = false;
        
        Swal.fire({
          title: '¡Cuenta confirmada!',
          text: response.Message || 'Tu contraseña ha sido establecida correctamente. Ahora puedes iniciar sesión.',
          icon: 'success',
          confirmButtonText: 'Ir a Login',
          confirmButtonColor: '#28a745'
        }).then(() => {
          this.router.navigate(['/auth/login']);
        });
      },
      error: (error) => {
        console.error('Error al activar cuenta:', error);
        this.isLoading = false;
        
        let errorMessage = 'Hubo un problema al activar tu cuenta. Intenta de nuevo.';
        
        // Manejar errores específicos
        if (error.status === 400) {
          errorMessage = 'El token es inválido o ha expirado.';
        } else if (error.status === 404) {
          errorMessage = 'No se encontró la cuenta para activar.';
        } else if (error.status === 409) {
          errorMessage = 'Esta cuenta ya ha sido activada.';
        } else if (error.error?.Message) {
          errorMessage = error.error.Message;
        }
        
        Swal.fire({
          title: 'Error al Activar Cuenta',
          text: errorMessage,
          icon: 'error',
          confirmButtonText: 'Entendido',
          confirmButtonColor: '#dc3545'
        });
      }
    });
  }

  // Marcar todos los campos como tocados para mostrar errores
  markFormGroupTouched(): void {
    Object.keys(this.confirmForm.controls).forEach(key => {
      const control = this.confirmForm.get(key);
      control?.markAsTouched();
    });
  }

  // Mostrar error y redirigir
  showErrorAndRedirect(message: string): void {
    Swal.fire({
      title: 'Error',
      text: message,
      icon: 'error',
      confirmButtonText: 'Ir a Login',
      confirmButtonColor: '#dc3545'
    }).then(() => {
      this.router.navigate(['/auth/login']);
    });
  }

  // Verificar si un campo tiene errores
  hasError(fieldName: string, errorType?: string): boolean {
    const field = this.confirmForm.get(fieldName);
    if (!field) return false;
    
    if (errorType) {
      return field.hasError(errorType) && (field.dirty || field.touched);
    }
    
    return field.invalid && (field.dirty || field.touched);
  }

  // Obtener mensaje de error específico
  getErrorMessage(fieldName: string): string {
    const field = this.confirmForm.get(fieldName);
    if (!field || !field.errors) return '';

    if (field.errors['required']) {
      return fieldName === 'password' ? 'La contraseña es requerida' : 'Confirma tu contraseña';
    }

    if (field.errors['minlength']) {
      return 'La contraseña debe tener al menos 8 caracteres';
    }

    if (field.errors['weakPassword']) {
      return 'La contraseña no cumple con los requisitos de seguridad';
    }

    return '';
  }

  // Verificar si las contraseñas no coinciden
  hasPasswordMismatch(): boolean {
    const confirmPasswordField = this.confirmForm.get('confirmPassword');
    return this.confirmForm.hasError('passwordMismatch') && 
           confirmPasswordField?.dirty && 
           confirmPasswordField?.touched;
  }
}
